import React, { useState, useMemo, useEffect } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    FormControlLabel,
    Paper,
    Divider,
    Tooltip,
    IconButton,
    Chip,
    CircularProgress,
    Alert
} from "@mui/material";
import HelpIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTheme } from "@mui/material/styles";
import { getCardColor } from "../../utils/colorUtils";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import LoadingSwitch from "../../components/LoadingSwitch";
import MainCard from "../../components/cards/MainCard";
import {
    useLazyGetLocationsByTenancyIdQuery,
    useLazyGetDepartmentsByTenancyIdQuery,
    useLazyGetCitiesByTenancyIdQuery,
    useLazyGetCountriesByTenancyIdQuery,
    useLazyGetCompanyNamesByTenancyIdQuery,
    useLazyGetStatesByTenancyIdQuery,
} from "../../store/services/tenancy.service";

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 900,
    height: '80vh',
    display: 'flex',
    flexDirection: 'column',
    bgcolor: 'background.default',
    borderRadius: 2,
    boxShadow: 24,
    p: 3,
};

const scopeLabels = {
    Tenancy: 'Tenancy',
    Group: 'Group',
    Location: 'Location',
    Department: 'Department',
    City: 'City',
    Country: 'Country',
    CompanyName: 'Company',
    State: 'State',
};

const categoryOptions = [
    { value: 'Group', label: 'Group' },
    { value: 'Department', label: 'Department' },
    { value: 'Location', label: 'Location' },
    { value: 'City', label: 'City' },
    { value: 'Country', label: 'Country' },
    { value: 'CompanyName', label: 'Company' },
    { value: 'State', label: 'State' },
];

AssignPackModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    contentPacks: PropTypes.array,
    groups: PropTypes.array,
    onConfirm: PropTypes.func,
    isLoading: PropTypes.bool,
    initialValues: PropTypes.object
};

export default function AssignPackModal({
    open,
    onClose,
    contentPacks = [],
    groups = [],
    onConfirm,
    isLoading = false,
    initialValues = null
}) {
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';
    const { tenancyId } = useSelector((state) => state.tenancy);

    const [selectedContentPack, setSelectedContentPack] = useState('');
    const [isTenancyWide, setIsTenancyWide] = useState(false);
    const [starterPack, setStarterPack] = useState(false);
    const [annualRefresh, setAnnualRefresh] = useState(false);
    const [showConfirmStep, setShowConfirmStep] = useState(false);
    const [sendToExistingChecked, setSendToExistingChecked] = useState(false);
    const [activeCategory, setActiveCategory] = useState('Group');

    const [leftSearch, setLeftSearch] = useState('');
    const [rightSearch, setRightSearch] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);

    // Lazy queries for different grouping types
    const [triggerLocations, { data: locationsData, isLoading: locationsLoading }] = useLazyGetLocationsByTenancyIdQuery();
    const [triggerDepartments, { data: departmentsData, isLoading: departmentsLoading }] = useLazyGetDepartmentsByTenancyIdQuery();
    const [triggerCities, { data: citiesData, isLoading: citiesLoading }] = useLazyGetCitiesByTenancyIdQuery();
    const [triggerCountries, { data: countriesData, isLoading: countriesLoading }] = useLazyGetCountriesByTenancyIdQuery();
    const [triggerCompanyNames, { data: companyNamesData, isLoading: companyNamesLoading }] = useLazyGetCompanyNamesByTenancyIdQuery();
    const [triggerStates, { data: statesData, isLoading: statesLoading }] = useLazyGetStatesByTenancyIdQuery();

    // Fetch data when category changes
    useEffect(() => {
        if (!open || !tenancyId) return;
        switch (activeCategory) {
            case 'Location': triggerLocations({ tenancyId }); break;
            case 'Department': triggerDepartments({ tenancyId }); break;
            case 'City': triggerCities({ tenancyId }); break;
            case 'Country': triggerCountries({ tenancyId }); break;
            case 'CompanyName': triggerCompanyNames({ tenancyId }); break;
            case 'State': triggerStates({ tenancyId }); break;
            default: break;
        }
    }, [open, tenancyId, activeCategory]);

    // Handle initialValues for legacy convert flow
    useEffect(() => {
        if (open && initialValues) {
            if (initialValues.contentPackId) setSelectedContentPack(initialValues.contentPackId);
            if (initialValues.grouping === 'Tenancy') setIsTenancyWide(true);
            if (initialValues.starterPack !== undefined) setStarterPack(initialValues.starterPack);
            if (initialValues.annualRefresh !== undefined) setAnnualRefresh(initialValues.annualRefresh);
        }
    }, [open, initialValues]);

    const groupItems = useMemo(() =>
        (groups || []).map(g => ({ id: g.userGroupId, name: g.name })),
    [groups]);

    // Get items for the currently active category
    const activeCategoryItems = useMemo(() => {
        switch (activeCategory) {
            case 'Group': return groupItems;
            case 'Location': return locationsData || [];
            case 'Department': return departmentsData || [];
            case 'City': return citiesData || [];
            case 'Country': return countriesData || [];
            case 'CompanyName': return companyNamesData || [];
            case 'State': return statesData || [];
            default: return [];
        }
    }, [activeCategory, groupItems, locationsData, departmentsData, citiesData, countriesData, companyNamesData, statesData]);

    const isCategoryLoading = (activeCategory === 'Location' && locationsLoading)
        || (activeCategory === 'Department' && departmentsLoading)
        || (activeCategory === 'City' && citiesLoading)
        || (activeCategory === 'Country' && countriesLoading)
        || (activeCategory === 'CompanyName' && companyNamesLoading)
        || (activeCategory === 'State' && statesLoading);

    // Filter out already selected items from available
    const selectedKey = (scope, id) => `${scope}::${id}`;
    const selectedKeys = useMemo(() =>
        new Set(selectedItems.map(item => selectedKey(item.scope, item.id))),
    [selectedItems]);

    const filteredAvailableItems = useMemo(() => {
        return activeCategoryItems
            .filter(item => !selectedKeys.has(selectedKey(activeCategory, item.id)))
            .filter(item => item.name.toLowerCase().includes(leftSearch.toLowerCase()));
    }, [activeCategoryItems, selectedKeys, activeCategory, leftSearch]);

    const handleAddItem = (item) => {
        const key = selectedKey(activeCategory, item.id);
        if (!selectedKeys.has(key)) {
            setSelectedItems(prev => [...prev, { ...item, scope: activeCategory }]);
        }
    };

    const handleRemoveItem = (item) => {
        setSelectedItems(prev => prev.filter(i => selectedKey(i.scope, i.id) !== selectedKey(item.scope, item.id)));
    };

    const handleCategoryChange = (event) => {
        setActiveCategory(event.target.value);
        setLeftSearch('');
    };

    const handleProceedToConfirm = () => {
        setShowConfirmStep(true);
    };

    const handleFinalConfirm = (sendToExistingUsers) => {
        if (onConfirm) {
            if (isTenancyWide) {
                onConfirm({
                    contentPackId: selectedContentPack,
                    starterPack,
                    annualRefresh,
                    sendToExistingUsers,
                    recipients: [{ scope: 'Tenancy', id: null, name: 'Tenancy' }]
                });
            } else {
                onConfirm({
                    contentPackId: selectedContentPack,
                    starterPack,
                    annualRefresh,
                    sendToExistingUsers,
                    recipients: selectedItems
                });
            }
        }
        handleClose();
    };

    const handleBackToAssign = () => {
        setShowConfirmStep(false);
        setSendToExistingChecked(false);
    };

    const handleClose = () => {
        setSelectedContentPack('');
        setIsTenancyWide(false);
        setStarterPack(false);
        setAnnualRefresh(false);
        setShowConfirmStep(false);
        setSendToExistingChecked(false);
        setLeftSearch('');
        setRightSearch('');
        setSelectedItems([]);
        setActiveCategory('Group');
        onClose();
    };

    // Group selected items by scope for the right panel
    const filteredSelectedItems = selectedItems.filter(item =>
        item.name.toLowerCase().includes(rightSearch.toLowerCase())
    );

    const filteredSelectedByScope = useMemo(() => {
        const grouped = {};
        filteredSelectedItems.forEach(item => {
            if (!grouped[item.scope]) grouped[item.scope] = [];
            grouped[item.scope].push(item);
        });
        return grouped;
    }, [filteredSelectedItems]);

    const confirmDisabled = !selectedContentPack || (!isTenancyWide && selectedItems.length === 0);

    const assignmentCount = isTenancyWide ? 1 : selectedItems.length;
    const scopeWord = assignmentCount > 1 ? 'scopes' : 'scope';
    // With both off, Create alone would save an assignment that never sends anything.
    const createDisabled = !starterPack && !annualRefresh;

    return (
        <>
        <Modal
            open={open && !showConfirmStep}
            onClose={handleClose}
            aria-labelledby="assign-pack-modal"
        >
            <Paper elevation={24} sx={modalStyle}>
                {/* Top Section */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
                    {/* Left side - Content pack & tenancy toggle */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Content pack</InputLabel>
                            <Select
                                value={selectedContentPack}
                                onChange={(e) => setSelectedContentPack(e.target.value)}
                                label="Content pack"
                                disabled={!!initialValues?.contentPackId}
                            >
                                {contentPacks.map((pack) => (
                                    <MenuItem key={pack.internalId} value={pack.internalId}>
                                        {pack.contentPackName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isTenancyWide}
                                    onChange={(e) => setIsTenancyWide(e.target.checked)}
                                    disabled={!!initialValues?.grouping}
                                />
                            }
                            label="Assign to entire tenancy"
                        />
                    </Box>

                    {/* Right side - Annual Refresh & Confirm */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleProceedToConfirm}
                            disabled={confirmDisabled}
                        >
                            {assignmentCount > 1 ? `Confirm (${assignmentCount} assignments)` : 'Confirm'}
                        </Button>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Sent automatically to users when they're added to the platform or join this group.">
                                <IconButton size="small">
                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                </IconButton>
                            </Tooltip>
                            <Typography variant="body2">Starter Pack</Typography>
                            <LoadingSwitch
                                checked={starterPack}
                                onChange={() => setStarterPack(prev => !prev)}
                                loading={false}
                                inputProps={{ "aria-label": "starter pack toggle" }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Re-sends this pack every year on the anniversary of each user's account creation date.">
                                <IconButton size="small">
                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                </IconButton>
                            </Tooltip>
                            <Typography variant="body2">Annual Refresh</Typography>
                            <LoadingSwitch
                                checked={annualRefresh}
                                onChange={() => setAnnualRefresh(prev => !prev)}
                                loading={false}
                                inputProps={{ "aria-label": "annual refresh toggle" }}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Bottom Section - Transfer Lists */}
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    ...(isTenancyWide && {
                        opacity: 0.4,
                        pointerEvents: 'none',
                    })
                }}>
                    {/* Left Panel - Available Items with category dropdown */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <MainCard
                            content={false}
                            boxShadow
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                bgcolor: getCardColor(lightMode),
                                overflow: 'hidden'
                            }}
                        >
                        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.65 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Category</InputLabel>
                                <Select
                                    value={activeCategory}
                                    onChange={handleCategoryChange}
                                    label="Category"
                                >
                                    {categoryOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={`Search ${scopeLabels[activeCategory] || ''}...`}
                                value={leftSearch}
                                onChange={(e) => setLeftSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
                            {isCategoryLoading ? (
                                <ListItem sx={{ justifyContent: 'center' }}>
                                    <CircularProgress size={24} />
                                </ListItem>
                            ) : filteredAvailableItems.length === 0 ? (
                                <ListItem>
                                    <ListItemText
                                        primary={`No ${scopeLabels[activeCategory]?.toLowerCase() || 'items'} available`}
                                        sx={{ color: 'text.secondary' }}
                                    />
                                </ListItem>
                            ) : (
                                filteredAvailableItems.map((item) => (
                                    <ListItem
                                        key={`${activeCategory}-${item.id}`}
                                        secondaryAction={
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={() => handleAddItem(item)}
                                                sx={{
                                                    minWidth: 32,
                                                    width: 32,
                                                    height: 32,
                                                    p: 0
                                                }}
                                            >
                                                <AddIcon fontSize="small" />
                                            </Button>
                                        }
                                        sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
                                    >
                                        <ListItemText primary={item.name} />
                                    </ListItem>
                                ))
                            )}
                        </List>
                        </MainCard>
                    </Box>

                    {/* Right Panel - Selected Items (grouped by category) */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <MainCard
                            content={false}
                            boxShadow
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                bgcolor: getCardColor(lightMode),
                                overflow: 'hidden'
                            }}
                        >
                        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body2" sx={{
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                fontWeight: 600,
                            }}>
                                To send to {selectedItems.length > 0 && `(${selectedItems.length})`}
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search..."
                                value={rightSearch}
                                onChange={(e) => setRightSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
                            {filteredSelectedItems.length === 0 ? (
                                <ListItem>
                                    <ListItemText
                                        primary="No items selected"
                                        sx={{ color: 'text.secondary' }}
                                    />
                                </ListItem>
                            ) : (
                                Object.entries(filteredSelectedByScope).map(([scope, items]) => (
                                    <React.Fragment key={scope}>
                                        <ListItem
                                            sx={{
                                                bgcolor: 'action.hover',
                                                py: 0.5,
                                                minHeight: 32,
                                            }}
                                        >
                                            <ListItemText
                                                primary={scopeLabels[scope] || scope}
                                                primaryTypographyProps={{
                                                    variant: 'caption',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    color: 'text.secondary',
                                                }}
                                            />
                                        </ListItem>
                                        {items.map((item) => (
                                            <ListItem
                                                key={`${scope}-${item.id}`}
                                                secondaryAction={
                                                    <Button
                                                        variant="contained"
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleRemoveItem(item)}
                                                        sx={{
                                                            minWidth: 32,
                                                            width: 32,
                                                            height: 32,
                                                            p: 0
                                                        }}
                                                    >
                                                        <RemoveIcon fontSize="small" />
                                                    </Button>
                                                }
                                                sx={{ borderBottom: `1px solid ${theme.palette.divider}`, pl: 3 }}
                                            >
                                                <ListItemText primary={item.name} />
                                            </ListItem>
                                        ))}
                                    </React.Fragment>
                                ))
                            )}
                        </List>
                        </MainCard>
                    </Box>
                </Box>
            </Paper>

        </Modal>

        {/* Confirmation Step Modal */}
        <Modal
            open={showConfirmStep}
            onClose={handleBackToAssign}
            aria-labelledby="confirm-send-modal"
        >
            <Paper sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 520,
                p: 3,
                borderRadius: 2,
                outline: 'none',
                bgcolor: 'background.default',
                boxShadow: 24,
            }}>
                <Typography variant="h6" gutterBottom>
                    Create {assignmentCount > 1 ? `${assignmentCount} Assignments` : 'Assignment'}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {isTenancyWide && (
                    <Box sx={{ mb: 2 }}>
                        <Chip
                            label="Entire Tenancy"
                            color="warning"
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                            This assignment will apply to all users in the tenancy.
                        </Typography>
                    </Box>
                )}

                {!isTenancyWide && assignmentCount > 0 && (
                    <>
                        {assignmentCount > 1 && (
                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                This will create a separate assignment for each selected scope:
                            </Typography>
                        )}
                        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selectedItems.map((item) => (
                                <Chip
                                    key={`${item.scope}-${item.id}`}
                                    label={`${scopeLabels[item.scope]}: ${item.name}`}
                                    size="small"
                                    color="secondary"
                                    sx={{ fontWeight: 500 }}
                                />
                            ))}
                        </Box>
                    </>
                )}

                <Typography variant="body2" sx={{ mb: 2 }}>
                    {createDisabled ? (
                        <>Starter Pack and Annual Refresh are both off, so this assignment would never send anything on its own. Turn one on, or send to existing users now.</>
                    ) : (
                        <>
                            Clicking <strong>Create</strong> will save the assignment.
                            {starterPack && ` Users who join the ${scopeWord} in the future will receive the content pack.`}
                            {annualRefresh && " Every user will receive it each year on the anniversary of their account creation date."}
                        </>
                    )}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={sendToExistingChecked}
                            onChange={(e) => setSendToExistingChecked(e.target.checked)}
                        />
                    }
                    label={
                        <Typography variant="body2" component="span">
                            I also want to send to <strong>all existing users</strong> right now
                        </Typography>
                    }
                    sx={{ mb: 1, alignItems: 'center' }}
                />

                {sendToExistingChecked && (
                    <Alert severity="warning" variant="outlined" sx={{ mb: 2, color: 'text.primary' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: 'warning.main' }}>
                            This will send immediately
                        </Typography>
                        <Typography variant="body2" color="inherit">
                            Every existing user in {isTenancyWide ? 'the entire tenancy' : (assignmentCount > 1 ? 'the selected scopes' : 'the selected scope')} will receive this content pack <strong>right now</strong>. This cannot be undone.
                        </Typography>
                    </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={handleBackToAssign}
                    >
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleFinalConfirm(false)}
                        disabled={createDisabled}
                    >
                        Create
                    </Button>
                    {sendToExistingChecked && (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleFinalConfirm(true)}
                        >
                            Create & Send Now
                        </Button>
                    )}
                </Box>
            </Paper>
        </Modal>
        </>
    );
}
